// src/app/services/brand/measured-funnel-api.ts
//
// The measured funnel behind the Brand Monitoring headline: statistical weight
// from real measurement runs, replacing the self-reported snapshot signal.
//
// Three metrics, always in this order: primary recommendation, top 5, mention
// rate. The server fixes that order in metricOrder and the UI must follow it
// rather than re-sorting. Primary recommendation leads everything.
import { apiFetch } from "@/api/api-fetch";

export type FunnelMetric = "primary_rec_rate" | "top5_rate" | "mention_rate";

/** "measurement" when real run data is behind the numbers; "monitoring_snapshot"
 *  when the brand has no usable measurement run and the caller must fall back
 *  to monitoring data and label it as a snapshot. */
export type FunnelSource = "measurement" | "monitoring_snapshot";

export interface MeasuredFunnelRun {
  id: number;
  label: string | null;
  sequence: number | null;
  completedAt: string | null;
  /** A pre-signing prospect read standing in while the brand has no client
   *  run. Never a baseline; the page labels it as a prospect read. */
  isProspectRead?: boolean;
  /** Repeats per question this run used (3 for a prospect read). */
  reps?: number | null;
}

export interface MeasuredFunnel {
  source: FunnelSource;
  brand: string;
  customerId: number | null;
  run: MeasuredFunnelRun | null;
  baselineRun: { id: number; sequence: number | null; lanes: string[] } | null;
  /** Rates as 0..1, or null when there is no usable measurement run. */
  funnel: Record<FunnelMetric, number> | null;
  lanes: { used: string[]; notInBaseline: string[]; excluded: string[] };
  metricOrder: FunnelMetric[];
  labels: Record<FunnelMetric, string>;
}

/** Canonical order, mirrored from the server so the UI has a safe default if a
 *  response ever arrives without metricOrder. */
export const FUNNEL_ORDER: FunnelMetric[] = [
  "primary_rec_rate",
  "top5_rate",
  "mention_rate",
];

export const FUNNEL_LABELS: Record<FunnelMetric, string> = {
  primary_rec_rate: "Primary recommendation",
  top5_rate: "Top 5",
  mention_rate: "Mention rate",
};

/** What each metric means, for the tooltip under the headline. */
export const FUNNEL_DESCRIPTIONS: Record<FunnelMetric, string> = {
  primary_rec_rate:
    "How often the models put you forward as the recommendation to act on.",
  top5_rate: "How often you land in the top five options the models offer.",
  mention_rate: "How often the models mention you at all.",
};

/**
 * The three funnel rates from a brand's most recent completed measurement run,
 * blended across baseline-eligible lanes only. Read-only.
 */
export async function fetchMeasuredFunnel(brand: string): Promise<MeasuredFunnel> {
  const params = new URLSearchParams();
  params.set("brand", brand);
  return apiFetch<MeasuredFunnel>(`/monitoring/measured-funnel?${params.toString()}`);
}
